import React, { useEffect, useState, useMemo } from "react";
import Menu from "../panel/Menu";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  MenuItem,
  Pagination
} from "@mui/material";
import { Add, Edit, DeleteOutline } from "@mui/icons-material";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDoc
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import Swal from "sweetalert2";

function Extraservices() {

  /* ================= STATE ================= */
  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [plans, setPlans] = useState([]);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const [form, setForm] = useState({
    servicename: "",
    amount: "",
    status: "active"
  });

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => setUserData(user));
    return () => unsub();
  }, []);

  /* ================= FETCH PROPERTY ================= */
  useEffect(() => {
    const fetchRole = async () => {
      if (!userData) return;

      const roleRef = doc(db, "authroles", userData.uid);
      const snap = await getDoc(roleRef);

      if (snap.exists()) {
        setPropertyId(snap.data().propertyId || null);
      } else {
        Swal.fire("Error", "Property not assigned", "error");
      }
    };

    fetchRole();
  }, [userData]);

  /* ================= FETCH SERVICES ================= */
  const fetchPlans = async () => {
    if (!propertyId) return;

    const q = query(
      collection(db, "extraservices"),
      where("propertyId", "==", propertyId)
    );

    const snap = await getDocs(q);
    setPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchPlans();
  }, [propertyId]);

  /* ================= SAVE ================= */
  const savePlan = async () => {
    if (!form.servicename || !form.amount) {
      Swal.fire("Required", "Service name & amount required", "warning");
      return;
    }

    try {
      if (editId) {
        await updateDoc(doc(db, "extraservices", editId), {
          ...form,
          amount: Number(form.amount)
        });
        Swal.fire("Updated", "Extra Service updated", "success");
      } else {
        await addDoc(collection(db, "extraservices"), {
          ...form,
          amount: Number(form.amount),
          propertyId,
          createdAt: serverTimestamp()
        });
        Swal.fire("Added", "Extra Service added", "success");
      }

      setOpen(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  /* ================= DELETE ================= */
  const deletePlan = async (id) => {
    const ok = await Swal.fire({
      title: "Delete Extra Service?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });

    if (ok.isConfirmed) {
      await deleteDoc(doc(db, "extraservices", id));
      Swal.fire("Deleted", "Extra Service removed", "success");
      fetchPlans();
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setEditId(null);
    setForm({ servicename: "", amount: "", status: "active" });
  };

  const openEdit = (plan) => {
    setEditId(plan.id);
    setForm({
      servicename: plan.servicename,
      amount: plan.amount,
      status: plan.status
    });
    setOpen(true);
  };

  /* ================= FILTER + PAGINATION ================= */
  const filteredPlans = useMemo(() => {
    return plans.filter(p =>
      p.servicename?.toLowerCase().includes(search.toLowerCase())
    );
  }, [plans, search]);

  const totalPages = Math.ceil(filteredPlans.length / rowsPerPage);

  const paginatedPlans = filteredPlans.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  /* ================= UI ================= */
  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Menu />

      <Box className="content-wrapper" flex={1} p={4}>
        <Stack direction="row" justifyContent="space-between" mb={3}>
          <Typography fontSize={26} fontWeight={800}>
            Extra Services
          </Typography>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            disabled={!propertyId}
          >
            Add Extra Service
          </Button>
        </Stack>

        {/* SEARCH */}
        <TextField
          placeholder="Search Service..."
          size="small"
          fullWidth
          sx={{ mb: 2, maxWidth: 300 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sr No</TableCell>
                    <TableCell>Service Name</TableCell>
                    <TableCell>Amount (₹)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedPlans.map((p, index) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{p.servicename}</TableCell>
                      <TableCell>₹ {p.amount}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.status.toUpperCase()}
                          color={p.status === "active" ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEdit(p)}>
                          <Edit />
                        </IconButton>
                        <IconButton color="error" onClick={() => deletePlan(p.id)}>
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!paginatedPlans.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No Extra Services found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <Stack alignItems="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* DIALOG */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editId ? "Edit Extra Service" : "Add Extra Service"}
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Service Name"
                value={form.servicename}
                onChange={(e) =>
                  setForm({ ...form, servicename: e.target.value })
                }
                fullWidth
                size="small"
              />

              <TextField
                label="Amount"
                type="number"
                value={form.amount}
                onChange={(e) =>
                  setForm({ ...form, amount: e.target.value })
                }
                fullWidth
                size="small"
              />

              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value })
                }
                fullWidth
                size="small"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={savePlan}>
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default Extraservices;
